/**
 * Security Module - Security and privacy awareness training
 *
 * Features:
 * - Role-aware content depth
 * - Interactive security scenarios
 * - Security pledge commitment
 * - Quiz with random question sampling
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Shield,
  Lock,
  AlertTriangle,
  Eye,
  FileWarning,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  FileText,
  Server,
  HardDrive,
  Users,
} from 'lucide-react';
import type { OnboardingRole, QuizAttempt, SecurityScenario } from '../../types/onboarding';
import { SECURITY_TRAINING_DEPTH } from '../../lib/onboarding/steps';
import {
  SECURITY_QUIZ_POOL,
  SECURITY_SCENARIOS,
} from '../../lib/onboarding/quizData';
import QuizEngine from './QuizEngine';
import { StepSection, StepCard } from './OnboardingStep';
import { cn } from '../../lib/utils';

interface SecurityModuleProps {
  onComplete: () => void;
  role: OnboardingRole;
}

type ModulePhase = 'training' | 'scenarios' | 'pledge' | 'quiz';

export default function SecurityModule({
  onComplete,
  role,
}: SecurityModuleProps) {
  const [phase, setPhase] = useState<ModulePhase>('training');
  const [completedTopics, setCompletedTopics] = useState<string[]>([]);
  const [completedScenarios, setCompletedScenarios] = useState<string[]>([]);
  const [pledgeAccepted, setPledgeAccepted] = useState(false);
  const [quizAttempts, setQuizAttempts] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);

  // Get role-specific configuration
  const trainingDepth = SECURITY_TRAINING_DEPTH[role];
  const requiredScenarios = trainingDepth.scenarios;

  // Get scenarios for this role
  const scenarios = useMemo(() => {
    return SECURITY_SCENARIOS.slice(0, requiredScenarios);
  }, [requiredScenarios]);

  // Training topics based on role depth
  const trainingTopics = useMemo(() => {
    const topics: TrainingTopic[] = [];

    // Data Handling - All roles get some level
    if (trainingDepth.dataHandling !== 'minimal') {
      topics.push({
        id: 'data-handling',
        title: 'Data Handling & Classification',
        icon: FileWarning,
        depth: trainingDepth.dataHandling,
        content: DATA_HANDLING_CONTENT[trainingDepth.dataHandling] || DATA_HANDLING_CONTENT.minimal,
      });
    } else {
      topics.push({
        id: 'data-handling',
        title: 'Data Basics',
        icon: FileWarning,
        depth: 'minimal',
        content: DATA_HANDLING_CONTENT.minimal,
      });
    }

    // Incident Response
    topics.push({
      id: 'incident-response',
      title: 'Incident Response',
      icon: AlertTriangle,
      depth: trainingDepth.incidentResponse,
      content: INCIDENT_RESPONSE_CONTENT[trainingDepth.incidentResponse] || INCIDENT_RESPONSE_CONTENT.minimal,
    });

    // Access Control
    topics.push({
      id: 'access-control',
      title: 'Access Control & Authentication',
      icon: Lock,
      depth: trainingDepth.accessControl,
      content: ACCESS_CONTROL_CONTENT[trainingDepth.accessControl] || ACCESS_CONTROL_CONTENT.minimal,
    });

    // Privacy (all roles)
    topics.push({
      id: 'privacy',
      title: 'Privacy & Compliance',
      icon: Eye,
      depth: trainingDepth.dataHandling === 'comprehensive' ? 'full' : 'standard',
      content: PRIVACY_CONTENT[trainingDepth.dataHandling === 'comprehensive' ? 'full' : 'standard'],
    });

    return topics;
  }, [trainingDepth]);

  // Handle topic completion
  const handleTopicComplete = useCallback((topicId: string) => {
    setCompletedTopics((prev) => {
      if (prev.includes(topicId)) return prev;
      return [...prev, topicId];
    });
  }, []);

  // Check if training is complete
  const isTrainingComplete = completedTopics.length >= trainingTopics.length;

  // Check if scenarios are complete
  const isScenariosComplete = completedScenarios.length >= requiredScenarios;

  // Handle scenario completion
  const handleScenarioComplete = useCallback((scenarioId: string) => {
    setCompletedScenarios((prev) => {
      if (prev.includes(scenarioId)) return prev;
      return [...prev, scenarioId];
    });
  }, []);

  // Handle quiz completion
  const handleQuizComplete = useCallback(
    (result: QuizAttempt) => {
      setQuizAttempts((prev) => prev + 1);
      setLastScore(result.percentage);

      if (result.passed) {
        onComplete();
      }
    },
    [onComplete]
  );

  // Navigate to next phase
  const handleNextPhase = useCallback(() => {
    if (phase === 'training' && isTrainingComplete) {
      setPhase('scenarios');
    } else if (phase === 'scenarios' && isScenariosComplete) {
      setPhase('pledge');
    } else if (phase === 'pledge' && pledgeAccepted) {
      setPhase('quiz');
    }
  }, [phase, isTrainingComplete, isScenariosComplete, pledgeAccepted]);

  // Render training phase
  if (phase === 'training') {
    return (
      <div className="space-y-6">
        {/* Introduction */}
        <StepCard variant="highlight">
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
            <div>
              <h3
                className="font-semibold text-lg mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Security Awareness Training
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Protecting LUKHAS AI and our research requires everyone's commitment to security.
                Complete each topic below to proceed.
              </p>
            </div>
          </div>
        </StepCard>

        {/* Progress */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Progress: {completedTopics.length} of {trainingTopics.length} topics
          </span>
          <span className="text-sm font-medium" style={{ color: 'var(--color-accent)' }}>
            {Math.round((completedTopics.length / trainingTopics.length) * 100)}%
          </span>
        </div>

        {/* Training Topics */}
        <div className="space-y-4">
          {trainingTopics.map((topic) => (
            <TrainingTopicCard
              key={topic.id}
              topic={topic}
              isComplete={completedTopics.includes(topic.id)}
              onComplete={() => handleTopicComplete(topic.id)}
            />
          ))}
        </div>

        {/* Continue Button */}
        {isTrainingComplete && (
          <button
            onClick={handleNextPhase}
            className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01]"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            Continue to Scenarios
          </button>
        )}
      </div>
    );
  }

  // Render scenarios phase
  if (phase === 'scenarios') {
    return (
      <div className="space-y-6">
        {/* Introduction */}
        <StepCard>
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 flex-shrink-0" style={{ color: 'var(--color-warning)' }} />
            <div>
              <h3
                className="font-semibold text-lg mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Interactive Security Scenarios
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Test your security awareness with real-world scenarios.
                Complete {requiredScenarios} scenario{requiredScenarios > 1 ? 's' : ''} to continue.
              </p>
            </div>
          </div>
        </StepCard>

        {/* Scenarios */}
        <div className="space-y-4">
          {scenarios.map((scenario) => (
            <ScenarioCard
              key={scenario.id}
              scenario={scenario}
              isComplete={completedScenarios.includes(scenario.id)}
              onComplete={() => handleScenarioComplete(scenario.id)}
            />
          ))}
        </div>

        {/* Continue Button */}
        {isScenariosComplete && (
          <button
            onClick={handleNextPhase}
            className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01]"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            Continue to Pledge
          </button>
        )}
      </div>
    );
  }

  // Render pledge phase
  if (phase === 'pledge') {
    return (
      <div className="space-y-6">
        <StepCard variant="highlight">
          <div className="text-center mb-6">
            <Shield
              className="w-16 h-16 mx-auto mb-4"
              style={{ color: 'var(--color-accent)' }}
            />
            <h3
              className="text-xl font-bold mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Security Commitment Pledge
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              By accepting this pledge, you commit to upholding LUKHAS AI's security standards.
            </p>
          </div>

          {/* Pledge Content */}
          <div
            className="p-4 rounded-lg mb-6 text-sm space-y-3"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <p>I understand and commit to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Protecting confidential information and intellectual property</li>
              <li>Using strong passwords and multi-factor authentication</li>
              <li>Reporting security incidents immediately</li>
              <li>Following data handling and classification guidelines</li>
              <li>Being vigilant against phishing and social engineering</li>
              <li>Locking my workstation when stepping away</li>
              <li>Only using approved software and systems</li>
              <li>Not sharing credentials or bypassing security controls</li>
            </ul>
          </div>

          {/* Checkbox */}
          <label
            className={cn(
              'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors'
            )}
            style={{
              backgroundColor: pledgeAccepted
                ? 'var(--color-accent-muted)'
                : 'var(--color-bg-tertiary)',
              border: pledgeAccepted ? '2px solid var(--color-accent)' : '2px solid transparent',
            }}
          >
            <input
              type="checkbox"
              checked={pledgeAccepted}
              onChange={(e) => setPledgeAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
              style={{ accentColor: 'var(--color-accent)' }}
            />
            <span
              className="font-medium"
              style={{ color: 'var(--color-text-primary)' }}
            >
              I have read, understood, and commit to following these security practices
            </span>
          </label>
        </StepCard>

        {/* Continue Button */}
        <button
          onClick={handleNextPhase}
          disabled={!pledgeAccepted}
          className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-bg)',
          }}
        >
          Continue to Quiz
        </button>
      </div>
    );
  }

  // Render quiz phase
  return (
    <div className="space-y-6">
      <StepSection
        title="Security Knowledge Quiz"
        description="Test your understanding of security practices. You need 80% to pass."
      >
        <QuizEngine
          quizType="security"
          questionPool={SECURITY_QUIZ_POOL}
          onComplete={handleQuizComplete}
          previousAttempts={quizAttempts}
          previousScore={lastScore}
        />
      </StepSection>
    </div>
  );
}

// ============================================================================
// Training Topic Card Component
// ============================================================================

interface TrainingTopic {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  depth: string;
  content: TopicContent;
}

interface TopicContent {
  sections: {
    title: string;
    items: string[];
    icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  }[];
  keyTakeaway: string;
}

interface TrainingTopicCardProps {
  topic: TrainingTopic;
  isComplete: boolean;
  onComplete: () => void;
}

function TrainingTopicCard({ topic, isComplete, onComplete }: TrainingTopicCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasRead, setHasRead] = useState(isComplete);

  const Icon = topic.icon;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: `1px solid ${isComplete ? 'var(--color-success)' : 'var(--color-border)'}`,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              backgroundColor: isComplete
                ? 'rgba(34, 197, 94, 0.15)'
                : 'var(--color-bg-tertiary)',
            }}
          >
            {isComplete ? (
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
            ) : (
              <Icon className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
            )}
          </div>
          <div>
            <h4
              className="font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {topic.title}
            </h4>
            <span
              className="text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {topic.depth === 'comprehensive' || topic.depth === 'full'
                ? 'Comprehensive'
                : topic.depth === 'detailed' || topic.depth === 'standard'
                ? 'Standard'
                : 'Essential'}
            </span>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div
          className="px-4 pb-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {topic.content.sections.map((section, idx) => {
            const SectionIcon = section.icon;
            return (
              <div
                key={idx}
                className="p-4 rounded-lg"
                style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {SectionIcon && (
                    <SectionIcon className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                  )}
                  <h5
                    className="font-medium text-sm"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {section.title}
                  </h5>
                </div>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {section.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2">
                      <span className="text-xs mt-1" style={{ color: 'var(--color-accent)' }}>
                        •
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Key Takeaway */}
          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--color-accent-muted)',
              borderLeft: '3px solid var(--color-accent)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--color-accent)' }}
            >
              Key Takeaway: {topic.content.keyTakeaway}
            </p>
          </div>

          {/* Mark Complete Button */}
          {!isComplete && (
            <label
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors'
              )}
              style={{
                backgroundColor: hasRead ? 'var(--color-accent-muted)' : 'var(--color-bg-tertiary)',
              }}
            >
              <input
                type="checkbox"
                checked={hasRead}
                onChange={(e) => {
                  setHasRead(e.target.checked);
                  if (e.target.checked) {
                    onComplete();
                  }
                }}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--color-accent)' }}
              />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                I have read and understood this section
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Scenario Card Component
// ============================================================================

interface ScenarioCardProps {
  scenario: SecurityScenario;
  isComplete: boolean;
  onComplete: () => void;
}

function ScenarioCard({ scenario, isComplete, onComplete }: ScenarioCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleSelectOption = (optionId: string) => {
    if (showFeedback) return;
    setSelectedOption(optionId);
    setShowFeedback(true);

    // Mark complete if correct
    const option = scenario.options.find((o) => o.id === optionId);
    if (option?.isCorrect) {
      setTimeout(() => onComplete(), 500);
    }
  };

  const handleRetry = () => {
    setSelectedOption(null);
    setShowFeedback(false);
  };

  const selectedOptionData = scenario.options.find((o) => o.id === selectedOption);
  const isCorrect = selectedOptionData?.isCorrect;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: `1px solid ${isComplete ? 'var(--color-success)' : 'var(--color-border)'}`,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              backgroundColor: isComplete
                ? 'rgba(34, 197, 94, 0.15)'
                : 'var(--color-bg-tertiary)',
            }}
          >
            {isComplete ? (
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
            ) : scenario.id === 'scenario-1' ? (
              <Mail className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
            ) : scenario.id === 'scenario-2' ? (
              <Phone className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
            ) : (
              <FileText className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
            )}
          </div>
          <div>
            <h4
              className="font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {scenario.title}
            </h4>
            <span
              className="text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {scenario.description}
            </span>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Scenario Text */}
          <div
            className="p-4 rounded-lg whitespace-pre-wrap text-sm font-mono"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {scenario.scenario}
          </div>

          {/* Options */}
          <div className="space-y-2">
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              What should you do?
            </p>
            {scenario.options.map((option) => {
              const isSelected = selectedOption === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelectOption(option.id)}
                  disabled={showFeedback}
                  className={cn(
                    'w-full p-3 rounded-lg text-left text-sm transition-all',
                    'disabled:cursor-default'
                  )}
                  style={{
                    backgroundColor: showFeedback
                      ? option.isCorrect
                        ? 'rgba(34, 197, 94, 0.15)'
                        : isSelected
                        ? 'rgba(239, 68, 68, 0.15)'
                        : 'var(--color-bg-tertiary)'
                      : isSelected
                      ? 'var(--color-accent-muted)'
                      : 'var(--color-bg-tertiary)',
                    border: `2px solid ${
                      showFeedback
                        ? option.isCorrect
                          ? 'var(--color-success)'
                          : isSelected
                          ? 'var(--color-error)'
                          : 'transparent'
                        : isSelected
                        ? 'var(--color-accent)'
                        : 'transparent'
                    }`,
                  }}
                >
                  <span
                    style={{
                      color: showFeedback
                        ? option.isCorrect
                          ? 'var(--color-success)'
                          : isSelected
                          ? 'var(--color-error)'
                          : 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)',
                    }}
                  >
                    {option.text}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {showFeedback && selectedOptionData && (
            <div
              className="p-4 rounded-lg animate-in fade-in slide-in-from-bottom-2 duration-200"
              style={{
                backgroundColor: isCorrect
                  ? 'rgba(34, 197, 94, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${isCorrect ? 'var(--color-success)' : 'var(--color-error)'}`,
              }}
            >
              <p
                className="font-medium mb-2"
                style={{ color: isCorrect ? 'var(--color-success)' : 'var(--color-error)' }}
              >
                {isCorrect ? 'Correct!' : 'Not quite right'}
              </p>
              <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                {selectedOptionData.feedback}
              </p>
              {!isCorrect && (
                <button
                  onClick={handleRetry}
                  className="text-sm font-medium"
                  style={{ color: 'var(--color-accent)' }}
                >
                  Try Again
                </button>
              )}
              {isCorrect && !isComplete && (
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  {scenario.explanation}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Training Content by Depth
// ============================================================================

const DATA_HANDLING_CONTENT: Record<string, TopicContent> = {
  comprehensive: {
    sections: [
      {
        title: 'Data Classification Levels',
        icon: FileWarning,
        items: [
          'PUBLIC: Marketing materials, published research, public docs',
          'INTERNAL: General business info, team communications',
          'CONFIDENTIAL: Customer data, financial records, strategy docs',
          'RESTRICTED: Constellation architecture, MATRIZ internals, security configs',
        ],
      },
      {
        title: 'Handling Requirements',
        icon: HardDrive,
        items: [
          'RESTRICTED data: Encrypted storage only, need-to-know access, audit logging',
          'CONFIDENTIAL: Approved systems only, no external sharing without approval',
          'Document all data transfers and maintain chain of custody',
          'Secure deletion required for all classified data disposal',
        ],
      },
      {
        title: 'External Sharing',
        icon: Users,
        items: [
          'All external sharing requires manager approval',
          'Use secure file sharing systems, never personal cloud',
          'NDAs must be signed before sharing confidential information',
          'Track all shared documents with expiration dates',
        ],
      },
    ],
    keyTakeaway: 'When in doubt, treat data as RESTRICTED and verify before sharing.',
  },
  standard: {
    sections: [
      {
        title: 'Data Classification Basics',
        icon: FileWarning,
        items: [
          'Data is classified by sensitivity: Public, Internal, Confidential, Restricted',
          'Your access level determines what data you can view',
          'Higher classification = stricter handling requirements',
        ],
      },
      {
        title: 'Safe Handling Practices',
        icon: HardDrive,
        items: [
          'Use only approved LUKHAS systems for data storage',
          'Never transfer data to personal devices or accounts',
          'Ask your manager before sharing data externally',
        ],
      },
    ],
    keyTakeaway: 'Keep work data on work systems and verify before sharing.',
  },
  focused: {
    sections: [
      {
        title: 'What You Need to Know',
        icon: FileWarning,
        items: [
          'Only access data necessary for your assigned tasks',
          'Use approved LUKHAS systems exclusively',
          'Report any accidental data exposure immediately',
        ],
      },
    ],
    keyTakeaway: 'Access only what you need, store only in approved systems.',
  },
  minimal: {
    sections: [
      {
        title: 'Essential Data Rules',
        icon: FileWarning,
        items: [
          'All LUKHAS data is confidential unless stated otherwise',
          'Never share data outside approved channels',
          'Report any concerns to your point of contact',
        ],
      },
    ],
    keyTakeaway: 'Keep all LUKHAS information confidential.',
  },
};

const INCIDENT_RESPONSE_CONTENT: Record<string, TopicContent> = {
  detailed: {
    sections: [
      {
        title: 'Types of Security Incidents',
        icon: AlertTriangle,
        items: [
          'Data breach: Unauthorized access to confidential information',
          'Malware: Suspicious software or system behavior',
          'Phishing: Fraudulent attempts to obtain credentials',
          'Physical: Lost devices, tailgating, unauthorized access',
          'Insider threat: Suspicious colleague behavior',
        ],
      },
      {
        title: 'Immediate Response Steps',
        icon: AlertTriangle,
        items: [
          '1. STOP: Do not try to investigate or fix it yourself',
          '2. PRESERVE: Don\'t delete evidence or modify affected systems',
          '3. REPORT: Email security@lukhas.ai immediately',
          '4. DOCUMENT: Note what, when, where, and how you discovered it',
        ],
      },
      {
        title: 'What to Include in Reports',
        icon: FileText,
        items: [
          'Exact time and date of discovery',
          'Systems or data potentially affected',
          'Actions taken before and after discovery',
          'Screenshots if safe to capture',
          'Contact information for follow-up',
        ],
      },
    ],
    keyTakeaway: 'Report first, then document. Never try to handle incidents alone.',
  },
  standard: {
    sections: [
      {
        title: 'Recognizing Incidents',
        icon: AlertTriangle,
        items: [
          'Unexpected system behavior or pop-ups',
          'Emails asking for credentials or sensitive info',
          'Unauthorized access attempts or unfamiliar logins',
          'Lost or stolen devices',
        ],
      },
      {
        title: 'How to Report',
        icon: Mail,
        items: [
          'Email security@lukhas.ai immediately',
          'Include: what happened, when, and what you observed',
          'Don\'t wait for "complete" information',
        ],
      },
    ],
    keyTakeaway: 'See something, say something. Report to security@lukhas.ai.',
  },
  basic: {
    sections: [
      {
        title: 'When to Report',
        icon: AlertTriangle,
        items: [
          'Suspicious emails or messages',
          'Unexpected system behavior',
          'Lost or stolen devices',
          'Any security concern',
        ],
      },
    ],
    keyTakeaway: 'When in doubt, report to security@lukhas.ai.',
  },
  minimal: {
    sections: [
      {
        title: 'Security Contact',
        icon: Mail,
        items: [
          'Report any security concerns to security@lukhas.ai',
          'Don\'t try to investigate yourself',
        ],
      },
    ],
    keyTakeaway: 'Report concerns to security@lukhas.ai immediately.',
  },
};

const ACCESS_CONTROL_CONTENT: Record<string, TopicContent> = {
  full: {
    sections: [
      {
        title: 'Password Requirements',
        icon: Lock,
        items: [
          'Minimum 16 characters (20+ recommended)',
          'Mix of uppercase, lowercase, numbers, symbols',
          'Never reuse passwords across systems',
          'Use a password manager for all credentials',
        ],
      },
      {
        title: 'Multi-Factor Authentication',
        icon: Server,
        items: [
          'MFA is mandatory for all LUKHAS accounts',
          'Use hardware security keys (preferred) or authenticator apps',
          'Never share MFA codes or recovery keys',
          'Report lost MFA devices immediately',
        ],
      },
      {
        title: 'Session Security',
        icon: Lock,
        items: [
          'Lock your screen when stepping away (CMD+L / WIN+L)',
          'Log out of systems when work is complete',
          'Don\'t save passwords in browsers',
          'Review active sessions regularly',
        ],
      },
      {
        title: 'Access Requests',
        icon: Users,
        items: [
          'Request only the access you need for your role',
          'Access is reviewed quarterly - unused access is removed',
          'Report any access you shouldn\'t have',
        ],
      },
    ],
    keyTakeaway: 'Strong passwords + MFA + locked screens = protected systems.',
  },
  standard: {
    sections: [
      {
        title: 'Password Best Practices',
        icon: Lock,
        items: [
          'Use 16+ character passwords',
          'Enable MFA on all accounts',
          'Never share credentials',
        ],
      },
      {
        title: 'Secure Your Workstation',
        icon: Server,
        items: [
          'Lock screen when stepping away',
          'Don\'t leave devices unattended',
          'Log out when done for the day',
        ],
      },
    ],
    keyTakeaway: 'Long passwords, MFA, and locked screens protect your accounts.',
  },
  basic: {
    sections: [
      {
        title: 'Essential Access Rules',
        icon: Lock,
        items: [
          'Use strong, unique passwords',
          'Always enable MFA when prompted',
          'Lock your screen when stepping away',
        ],
      },
    ],
    keyTakeaway: 'Strong passwords + MFA + lock screen.',
  },
  minimal: {
    sections: [
      {
        title: 'Account Security',
        icon: Lock,
        items: [
          'Create a strong password when setting up access',
          'Enable MFA using the setup instructions',
          'Never share your login credentials',
        ],
      },
    ],
    keyTakeaway: 'Keep your credentials private and enable MFA.',
  },
};

const PRIVACY_CONTENT: Record<string, TopicContent> = {
  full: {
    sections: [
      {
        title: 'GDPR Compliance',
        icon: Eye,
        items: [
          'Personal data requires a legal basis for processing',
          'Data subjects have rights: access, correction, deletion',
          'Data breaches must be reported within 72 hours',
          'Privacy by design in all systems and processes',
        ],
      },
      {
        title: 'Handling Personal Data',
        icon: Users,
        items: [
          'Minimize data collection to what\'s necessary',
          'Anonymize data where possible',
          'Set retention periods and delete when no longer needed',
          'Document all processing activities',
        ],
      },
      {
        title: 'Data Subject Requests',
        icon: FileText,
        items: [
          'Forward all requests to dpo@lukhas.ai immediately',
          'Responses are legally required within 30 days',
          'Never delete data without proper authorization',
        ],
      },
    ],
    keyTakeaway: 'Treat personal data with care and forward requests to the DPO.',
  },
  standard: {
    sections: [
      {
        title: 'Privacy Basics',
        icon: Eye,
        items: [
          'Personal data is protected by law (GDPR)',
          'Only collect and use data you\'re authorized to access',
          'Users can request to see or delete their data',
        ],
      },
      {
        title: 'Your Responsibility',
        icon: Users,
        items: [
          'Forward privacy requests to dpo@lukhas.ai',
          'Don\'t share personal data unnecessarily',
          'Report any privacy concerns',
        ],
      },
    ],
    keyTakeaway: 'Personal data is protected by law. Handle with care.',
  },
};
